import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, {
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    Connection,
    Edge,
    BackgroundVariant,
    NodeTypes,
    useReactFlow,
    applyNodeChanges,
    applyEdgeChanges,
    NodeChange,
    EdgeChange,
    ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import AgentSelector from '../../AgentSelector';
import { TextMessageNode } from '../components/TextMessageNode';
import { ImageMessageNode } from '../components/ImageMessageNode';
import { AudioMessageNode } from '../components/AudioMessageNode';
import { VideoMessageNode } from '../components/VideoMessageNode';
import { DocumentMessageNode } from '../components/DocumentMessageNode';
import { DeletableEdge } from '../components/DeletableEdge';
import { Button } from '../../../../../components/ui/button';
import { MessageSquare, ImagePlus, Music, Video, FileText, Workflow, Save, ArrowLeft, Smartphone } from 'lucide-react';
import { useNodeGroups } from '../../../hooks/useNodeGroups';
import { GroupNode, GroupControls } from '../components/GroupNode';
import { useTrainingFlow } from '../../../hooks/useTrainingFlow';
import { FlowSelector } from '../components/FlowSelector';
import { Switch } from '../../../../../components/ui/switch';
import { toast } from '../../../../../hooks/use-toast';
import { AgentSelectionView } from '../components/AgentSelectionView';
import { ExpandableTabs } from '../../../../../components/ui/expandable-tabs';
import { messagesApi } from '../../../services/messagesApi';
import { updateNodesWithFlowData, recalculateAfterNodeDeletion } from '../../../utils/nodeSequencing';
import { WhatsAppPreviewPanel } from '../components/WhatsAppPreviewPanel';
import { Agent } from '../../../types/agentTypes';
import { agentProductsApi } from '../../../services/agentProductsApi';
import { WorkflowActionsContext } from '../components/AddNodeMenu';

const nodeTypes: NodeTypes = {
    textMessage: TextMessageNode,
    imageMessage: ImageMessageNode,
    audioMessage: AudioMessageNode,
    videoMessage: VideoMessageNode,
    documentMessage: DocumentMessageNode,
};

const edgeTypes = {
    deletable: DeletableEdge,
};

const initialNodes = [
    {
        id: '1',
        type: 'textMessage',
        data: { label: 'Mensaje de Bienvenida', message: 'Hola! ¿En qué puedo ayudarte?' },
        position: { x: 100, y: 250 },
    },
];

const initialEdges: Edge[] = [];

const AgentTrainingContent: React.FC<{ agent?: Agent | null; trainingFlow?: any; loadingFlow?: boolean; isStandaloneMode?: boolean; workflowName?: string; onBack?: () => void }> = ({ agent, trainingFlow, isStandaloneMode = false, workflowName, onBack }) => {
    // En modo standalone (workflows independientes), no usar numero_telefono
    const initialSelected = isStandaloneMode ? '' : ((agent && (agent.phone ?? '')) || (trainingFlow && (trainingFlow.numero_telefono ?? '')) || '');
    const [selectedAgent, setSelectedAgent] = useState(initialSelected);
    const [activeView, setActiveView] = useState<'agente' | 'funnel'>('funnel');
    const [showWhatsappPreview, setShowWhatsappPreview] = useState(false);
    const [nodes, setNodes] = useNodesState(initialNodes);
    const [edges, setEdges] = useEdgesState(initialEdges);
    const { getViewport, fitView } = useReactFlow();
    const [viewport, setViewport] = useState(getViewport());
    const [showLoadModal, setShowLoadModal] = useState(false);
    const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
    const [showNavigationWarning, setShowNavigationWarning] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
    const [availableProducts, setAvailableProducts] = useState<Array<{ id: string; product_key: string; product_name: string }>>([]);

    const {
        groups,
        setGroups,
        createGroupForNode,
        createGroupFromConnection,
        deleteGroup,
        updateGroupColor,
        updateGroupInstruction,
        updateGroupLeadStatus,
        updateGroupProductKeys,
        updateAllGroupBounds,
        checkGroupIntegrity,
        getGroupForNode,
        removeNodeFromGroup,
    } = useNodeGroups();

    const {
        currentFlowId,
        currentFlowName,
        isSaving,
        isLoading: isFlowLoading,
        hasUnsavedChanges,
        isActive,
        saveFlow,
        loadFlow,
        activateCurrentFlow,
        deactivateCurrentFlow,
        syncFlowData,
    } = useTrainingFlow(nodes, edges, groups, setNodes, setEdges, setGroups, selectedAgent);

    // Cargar el training flow cuando se pasa como prop (modo standalone)
    useEffect(() => {
        if (isStandaloneMode && trainingFlow && trainingFlow.id) {
            loadFlow(trainingFlow.id);
        }
    }, [isStandaloneMode, trainingFlow?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const onConnect = useCallback(
        (params: Connection) => {
            // Validar que el nodo source no tenga ya una conexión saliente
            const sourceHasConnection = edges.some(edge => edge.source === params.source);

            if (sourceHasConnection) {
                toast.error('Cada nodo solo puede conectarse a un único nodo siguiente');
                return;
            }

            setEdges((eds) => addEdge({
                ...params,
                type: 'deletable',
                animated: true,
                style: { stroke: '#00FF7A', strokeWidth: 2 },
            }, eds));

            if (params.source && params.target) {
                createGroupFromConnection(params.source, params.target, nodes);
            }
        },
        [setEdges, createGroupFromConnection, nodes, edges]
    );

    // Load persisted preview state per-flow
    useEffect(() => {
        try {
            if (currentFlowId) {
                const raw = localStorage.getItem(`whatsappPreview:${currentFlowId}`);
                if (raw !== null) setShowWhatsappPreview(JSON.parse(raw));
            } else {
                const raw = localStorage.getItem('whatsappPreview:global');
                if (raw !== null) setShowWhatsappPreview(JSON.parse(raw));
            }
        } catch (e) {
            // ignore
        }
    }, [currentFlowId]);

    const onNodesChange = useCallback(
        (changes: NodeChange[]) => {
            // Filter out 'remove' changes — deletion is handled exclusively
            // by handleDeleteSelected to prevent accidental removal on Ctrl+click
            const safeChanges = changes.filter(c => c.type !== 'remove');
            setNodes((nds) => applyNodeChanges(safeChanges, nds));
        },
        [setNodes]
    );

    const handleDeleteSelected = useCallback(() => {
        const selectedNodeIds = nodes.filter(n => n.selected).map(n => n.id);
        if (selectedNodeIds.length === 0) return;

        setNodes((nds) => {
            const remaining = nds.filter(n => !selectedNodeIds.includes(n.id));
            return recalculateAfterNodeDeletion(remaining, edges, groups, currentFlowId);
        });

        selectedNodeIds.forEach(async (nodeId) => {
            const group = getGroupForNode(nodeId);
            if (group) {
                removeNodeFromGroup(nodeId, group.id, nodes);
            }
            try {
                await messagesApi.deleteMessage(nodeId);
                console.log('Mensaje eliminado de BD:', nodeId);
            } catch (error) {
                console.error('Error eliminando mensaje de BD:', error);
            }
        });
    }, [nodes, edges, groups, currentFlowId, setNodes, getGroupForNode, removeNodeFromGroup]);

    const onEdgesChange = useCallback(
        (changes: EdgeChange[]) => {
            console.log('🔵 Edge changes:', changes);

            const edgesBeforeChange = edges.length;

            // Aplicar cambios y obtener edges actualizados
            const updatedEdges = applyEdgeChanges(changes, edges);
            const edgesAfterChange = updatedEdges.length;

            console.log('📊 Edges antes:', edgesBeforeChange, '→ después:', edgesAfterChange);
            setEdges(updatedEdges);

            // Si se redujo el número de edges, significa que se eliminó una conexión
            if (edgesAfterChange < edgesBeforeChange) {
                console.log('🚨 Se eliminó una conexión, verificando integridad...');
                console.log('📦 Grupos actuales:', groups.length);

                // Verificar inmediatamente con los edges actualizados
                requestAnimationFrame(() => {
                    checkGroupIntegrity(nodes, updatedEdges);
                });
            }
        },
        [edges, setEdges, checkGroupIntegrity, nodes, groups]
    );

    useEffect(() => {
        updateAllGroupBounds(nodes);
    }, [nodes, updateAllGroupBounds]);

    // Crear grupos para nodos iniciales que no tengan grupo
    // Solo corre después de que el flow haya terminado de cargarse para evitar
    // crear grupos individuales antes de la reconciliación
    useEffect(() => {
        if (isFlowLoading) return;

        const timeout = setTimeout(() => {
            nodes.forEach(node => {
                const hasGroup = groups.some(group => group.nodeIds.includes(node.id));
                if (!hasGroup) {
                    createGroupForNode(node.id, nodes);
                }
            });
            // Recalcular límites después de crear grupos
            setTimeout(() => {
                updateAllGroupBounds(nodes);
            }, 150);
        }, 300);

        return () => clearTimeout(timeout);
    }, [nodes, groups, createGroupForNode, updateAllGroupBounds, isFlowLoading]);

    // Recalcular GLOBALMENTE todos los nodos cada vez que cambien nodos, edges o grupos
    useEffect(() => {
        if (!currentFlowId) return;

        const timeout = setTimeout(() => {
            setNodes((currentNodes) => {
                // Recalcular secuencia para TODOS los nodos
                const updatedNodes = updateNodesWithFlowData(currentNodes, edges, groups, currentFlowId);

                // Verificar si realmente hubo cambios antes de actualizar
                const hasChanges = updatedNodes.some((node, idx) => {
                    const oldNode = currentNodes[idx];
                    const newData = node.data as any;
                    const oldData = oldNode?.data as any;
                    return !oldNode ||
                        newData.sequenceOrder !== oldData.sequenceOrder ||
                        newData.groupId !== oldData.groupId;
                });

                return hasChanges ? updatedNodes : currentNodes;
            });
        }, 100);

        return () => clearTimeout(timeout);
    }, [nodes.length, edges.length, groups.length, currentFlowId]); // Solo usar lengths para evitar loops infinitos

    // Cargar productos disponibles cuando cambie el flowId
    useEffect(() => {
        const loadProducts = async () => {
            if (!currentFlowId) {
                setAvailableProducts([]);
                return;
            }

            try {
                console.log('🔵 Cargando productos para flowId:', currentFlowId);
                const products = await agentProductsApi.getProductsByFlowId(currentFlowId);
                console.log('✅ Productos cargados:', products);
                const mappedProducts = products.map(p => ({
                    id: p.id || '',
                    product_key: p.product_key,
                    product_name: p.product_name
                }));
                console.log('📦 Productos mapeados para GroupControls:', mappedProducts);
                setAvailableProducts(mappedProducts);
            } catch (error) {
                console.error('❌ Error cargando productos:', error);
                setAvailableProducts([]);
            }
        };

        loadProducts();
    }, [currentFlowId]);

    // Actualizar viewport para que los grupos se sincronicen con zoom/pan
    const onMove = useCallback(() => {
        const newViewport = getViewport();
        setViewport(newViewport);
    }, [getViewport]);

    // Ajustar el canvas cuando se carga o cuando cambia la vista
    useEffect(() => {
        if (activeView === 'funnel' && selectedAgent && nodes.length > 0) {
            setTimeout(() => {
                fitView({
                    padding: 0.2,
                    duration: 300,
                    maxZoom: 1,
                });
            }, 100);
        }
    }, [activeView, selectedAgent, nodes.length]); // Remover fitView de dependencias

    // Función para calcular posición inteligente del nuevo nodo
    const calculateNewNodePosition = useCallback(() => {
        if (nodes.length === 0) {
            return { x: 100, y: 250 };
        }

        // Encontrar el nodo más a la derecha (mayor X)
        const rightmostNode = nodes.reduce((rightmost, node) =>
            node.position.x > rightmost.position.x ? node : rightmost
            , nodes[0]);

        // Colocar el nuevo nodo 400px a la derecha del último
        return {
            x: rightmostNode.position.x + 400,
            y: rightmostNode.position.y,
        };
    }, [nodes]);

    const addTextNode = useCallback(() => {
        const position = calculateNewNodePosition();
        const newNode = {
            id: `text-${Date.now()}`,
            type: 'textMessage',
            data: {
                label: 'Nuevo Mensaje',
                message: '',
                flowId: currentFlowId,
                groupId: null,
                sequenceOrder: 1
            },
            position,
        };
        setNodes((nds) => {
            const updatedNodes = [...nds, newNode];
            // Crear grupo automáticamente para el nuevo nodo
            setTimeout(() => {
                createGroupForNode(newNode.id, updatedNodes);
            }, 50);
            return updatedNodes;
        });
    }, [setNodes, createGroupForNode, calculateNewNodePosition, currentFlowId]);

    const addImageNode = useCallback(() => {
        const position = calculateNewNodePosition();
        const newNode = {
            id: `image-${Date.now()}`,
            type: 'imageMessage',
            data: {
                label: 'Nueva Imagen',
                imageUrl: '',
                flowId: currentFlowId,
                groupId: null,
                sequenceOrder: 1
            },
            position,
        };
        setNodes((nds) => {
            const updatedNodes = [...nds, newNode as any];
            setTimeout(() => createGroupForNode(newNode.id, updatedNodes), 50);
            return updatedNodes;
        });
    }, [setNodes, createGroupForNode, calculateNewNodePosition, currentFlowId]);

    const addAudioNode = useCallback(() => {
        const position = calculateNewNodePosition();
        const newNode = {
            id: `audio-${Date.now()}`,
            type: 'audioMessage',
            data: {
                label: 'Nuevo Audio',
                audioUrl: '',
                flowId: currentFlowId,
                groupId: null,
                sequenceOrder: 1
            },
            position,
        };
        setNodes((nds) => {
            const updatedNodes = [...nds, newNode as any];
            setTimeout(() => createGroupForNode(newNode.id, updatedNodes), 50);
            return updatedNodes;
        });
    }, [setNodes, createGroupForNode, calculateNewNodePosition, currentFlowId]);

    const addVideoNode = useCallback(() => {
        const position = calculateNewNodePosition();
        const newNode = {
            id: `video-${Date.now()}`,
            type: 'videoMessage',
            data: {
                label: 'Nuevo Video',
                videoUrl: '',
                flowId: currentFlowId,
                groupId: null,
                sequenceOrder: 1
            },
            position,
        };
        setNodes((nds) => {
            const updatedNodes = [...nds, newNode as any];
            setTimeout(() => createGroupForNode(newNode.id, updatedNodes), 50);
            return updatedNodes;
        });
    }, [setNodes, createGroupForNode, calculateNewNodePosition, currentFlowId]);

    const addDocumentNode = useCallback(() => {
        const position = calculateNewNodePosition();
        const newNode = {
            id: `document-${Date.now()}`,
            type: 'documentMessage',
            data: {
                label: 'Nuevo Documento',
                documentUrl: '',
                flowId: currentFlowId,
                groupId: null,
                sequenceOrder: 1
            },
            position,
        };
        setNodes((nds) => {
            const updatedNodes = [...nds, newNode as any];
            setTimeout(() => createGroupForNode(newNode.id, updatedNodes), 50);
            return updatedNodes;
        });
    }, [setNodes, createGroupForNode, calculateNewNodePosition, currentFlowId]);

    // Agregar nodo conectado desde el menú "+" de un nodo
    const handleAddConnectedNode = useCallback((sourceId: string, nodeType: string) => {
        const sourceNode = nodes.find(n => n.id === sourceId);
        if (!sourceNode) return;

        if (edges.some(e => e.source === sourceId)) {
            toast.error('Este nodo ya tiene una conexión saliente');
            return;
        }

        const typePrefix = nodeType.replace('Message', '');
        const newId = `${typePrefix}-${Date.now()}`;
        const position = { x: sourceNode.position.x + 400, y: sourceNode.position.y };

        const dataByType: Record<string, Record<string, unknown>> = {
            textMessage: { label: 'Nuevo Mensaje', message: '' },
            imageMessage: { label: 'Nueva Imagen', imageUrl: '' },
            audioMessage: { label: 'Nuevo Audio', audioUrl: '' },
            videoMessage: { label: 'Nuevo Video', videoUrl: '' },
            documentMessage: { label: 'Nuevo Documento', documentUrl: '' },
        };

        const newNode = {
            id: newId,
            type: nodeType,
            data: {
                ...(dataByType[nodeType] || dataByType.textMessage),
                flowId: currentFlowId,
                groupId: null,
                sequenceOrder: 1,
            } as any,
            position,
        };

        const newEdge = {
            id: `e-${sourceId}-${newId}`,
            source: sourceId,
            target: newId,
            type: 'deletable' as const,
            animated: true,
            style: { stroke: '#00FF7A', strokeWidth: 2 },
        };

        setNodes(nds => [...nds, newNode] as any);
        setEdges(eds => [...eds, newEdge]);

        setTimeout(() => {
            createGroupFromConnection(sourceId, newId, [...nodes, newNode]);
        }, 50);
    }, [nodes, edges, currentFlowId, setNodes, setEdges, createGroupFromConnection]);

    // Advertencia antes de salir con cambios no guardados
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    // Recalcular dimensiones de grupos cuando los nodos cambien
    useEffect(() => {
        if (nodes.length > 0 && groups.length > 0) {
            updateAllGroupBounds(nodes);
        }
    }, [nodes, updateAllGroupBounds]);

    const handleSaveFlow = async () => {
        try {
            if (!currentFlowId) {
                // Si no hay ID, pedir nombre para crear nuevo flujo
                const nombre = prompt('Nombre del flujo:');
                if (!nombre?.trim()) return;
                await saveFlow(nombre, selectedAgent);
                toast.success('Flujo guardado exitosamente', {
                    duration: 3000,
                    position: 'bottom-left',
                });
            } else {
                // Si ya tiene ID, actualizar el flujo actual
                await saveFlow(currentFlowName, selectedAgent);
                toast.success('Flujo actualizado exitosamente', {
                    duration: 3000,
                    position: 'bottom-left',
                });
            }

            // Publicar todos los grupos automáticamente después de guardar
            const { trainingGroupsApi } = await import('../../../services/trainingGroupsApi');
            let publishedCount = 0;
            let skippedCount = 0;

            for (const group of groups) {
                if (group.instruction) {
                    try {
                        await trainingGroupsApi.upsertGroup(group, nodes, currentFlowId);
                        publishedCount++;
                    } catch (error) {
                        console.error(`Error al publicar grupo ${group.number}:`, error);
                    }
                } else {
                    skippedCount++;
                }
            }

            if (publishedCount > 0) {
                toast.success(`${publishedCount} grupo(s) publicado(s)`, {
                    duration: 2000,
                    position: 'bottom-left',
                });
            }

            if (skippedCount > 0) {
                toast.info(`${skippedCount} grupo(s) sin instrucción (no publicado)`, {
                    duration: 2000,
                    position: 'bottom-left',
                });
            }

            // Limpiar grupos huérfanos de la BD (grupos que ya no existen en el canvas)
            try {
                const existingDbGroups = await trainingGroupsApi.getGroupsByFlowId(currentFlowId);
                // La BD guarda group_id como el NÚMERO del grupo, comparar contra g.number.
                const activeGroupIds = new Set(groups.map(g => String(g.number)));
                for (const dbGroup of existingDbGroups) {
                    if (!activeGroupIds.has(dbGroup.group_id)) {
                        await trainingGroupsApi.deleteGroup(dbGroup.group_id, currentFlowId);
                    }
                }
            } catch (cleanupError) {
                console.error('Error limpiando grupos huérfanos:', cleanupError);
            }

            // Limpiar training_messages huérfanos (mensajes que ya no están en el canvas)
            try {
                const canvasNodeIds = new Set(nodes.map(n => n.id));
                const allDbMessages = await messagesApi.getMessagesByFlowId(currentFlowId);
                const orphanMessages = allDbMessages.filter(m => !canvasNodeIds.has(m.node_id));
                if (orphanMessages.length > 0) {
                    await Promise.all(orphanMessages.map(m => messagesApi.deleteMessage(m.node_id)));
                    console.log(`🧹 Eliminados ${orphanMessages.length} mensajes huérfanos de training_messages`);
                }
            } catch (cleanupError) {
                console.error('Error limpiando mensajes huérfanos:', cleanupError);
            }
        } catch (error) {
            console.error('Error al guardar:', error);
            toast.error('Error al guardar el flujo', {
                duration: 3000,
                position: 'bottom-left',
            });
        }
    };

    const handleDiscardChanges = () => {
        if (pendingNavigation) {
            pendingNavigation();
        }
        setShowUnsavedWarning(false);
        setShowNavigationWarning(false);
        setPendingNavigation(null);
    };

    const handleCancelDiscard = () => {
        setShowUnsavedWarning(false);
        setShowNavigationWarning(false);
        setPendingNavigation(null);
    };

    const handleSaveAndContinue = async () => {
        await handleSaveFlow();
        if (pendingNavigation) {
            // Dar tiempo para que se guarde antes de continuar
            setTimeout(() => {
                pendingNavigation();
                setShowUnsavedWarning(false);
                setShowNavigationWarning(false);
                setPendingNavigation(null);
            }, 500);
        }
    };

    const handleActivateFlow = async () => {
        try {
            if (isActive) {
                await deactivateCurrentFlow();
                toast.success('Flujo desactivado', {
                    duration: 3000,
                    position: 'bottom-left',
                });
            } else {
                await activateCurrentFlow();
                toast.success('Flujo activado exitosamente', {
                    duration: 3000,
                    position: 'bottom-left',
                });
            }
        } catch (error) {
            console.error('Error al cambiar estado:', error);
            toast.error(error instanceof Error ? error.message : 'Error al cambiar estado del flujo', {
                duration: 3000,
                position: 'bottom-left',
            });
        }
    };

    // Función para publicar un grupo en training_groups (ya no se usa individualmente, se publica con el guardado)

    return (
        <div className="flex flex-col h-[calc(100vh-84px)] bg-background overflow-hidden">
            {/* Header STICKY */}
            <div className="sticky top-0 z-[60] flex items-center justify-between px-6 py-3 border-b bg-white shadow-sm">
                <div className="flex items-center gap-4 flex-1">
                    {/* Botón de volver (solo en modo standalone) */}
                    {isStandaloneMode && onBack && (
                        <button
                            onClick={onBack}
                            className="h-10 w-10 bg-grey-100 hover:bg-grey-200 rounded-full transition-colors flex items-center justify-center flex-shrink-0"
                            title="Volver a workflows"
                        >
                            <ArrowLeft className="w-5 h-5 text-foreground" />
                        </button>
                    )}

                    <div className="flex-1">
                        <div className="flex items-center gap-3">
                            <h1 className="text-lg font-semibold text-foreground">
                                {isStandaloneMode && workflowName ? workflowName : currentFlowName}
                            </h1>
                            {currentFlowId && (
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={isActive}
                                        onCheckedChange={handleActivateFlow}
                                        className="data-[state=checked]:bg-green-500"
                                    />
                                    <span className={`text-xs font-medium ${isActive ? 'text-green-600' : 'text-muted-foreground'}`}>
                                        {isActive ? 'Activo' : 'Desactivado'}
                                    </span>
                                </div>
                            )}
                            {hasUnsavedChanges && (
                                <span className="text-xs text-orange-600">• Sin guardar</span>
                            )}
                        </div>
                    </div>

                    {!isStandaloneMode && selectedAgent && (
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant={activeView === 'funnel' ? 'default' : 'ghost'}
                                className="gap-2 h-8"
                                onClick={() => setActiveView('funnel')}
                            >
                                <Workflow className="h-4 w-4" />
                                Workflow
                            </Button>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {(isStandaloneMode || (selectedAgent && activeView === 'funnel')) && (
                        <Button
                            size="sm"
                            variant={hasUnsavedChanges ? 'default' : 'outline'}
                            className="gap-2 h-8"
                            onClick={handleSaveFlow}
                            disabled={isSaving}
                        >
                            <Save className="h-4 w-4" />
                            {isSaving ? 'Guardando...' : 'Guardar'}
                        </Button>
                    )}
                    {!isStandaloneMode && selectedAgent && (
                        <AgentSelector
                            selectedAgent={selectedAgent}
                            onAgentChange={setSelectedAgent}
                        />
                    )}
                </div>
            </div>

            {/* Canvas */}
            <div className="flex-1 w-full relative flex overflow-hidden">
                {/* Sidebar izquierda con botones de nodos */}
                {(isStandaloneMode || (selectedAgent && activeView === 'funnel')) && currentFlowId && (
                    <div className="w-20 bg-white border-r flex flex-col items-center py-6 z-40">
                        <ExpandableTabs
                            orientation="vertical"
                            tabs={[
                                {
                                    title: "Texto",
                                    icon: MessageSquare,
                                    onClick: addTextNode,
                                    color: "text-primary"
                                },
                                {
                                    title: "Imagen",
                                    icon: ImagePlus,
                                    onClick: addImageNode,
                                    color: "text-secondary"
                                },
                                {
                                    title: "Audio",
                                    icon: Music,
                                    onClick: addAudioNode,
                                    color: "text-purple-500"
                                },
                                {
                                    title: "Video",
                                    icon: Video,
                                    onClick: addVideoNode,
                                    color: "text-orange-500"
                                },
                                {
                                    title: "Documento",
                                    icon: FileText,
                                    onClick: addDocumentNode,
                                    color: "text-indigo-500"
                                },
                            ]}
                            className="border-0 shadow-none bg-transparent p-0"
                        />
                    </div>
                )}

                {/* Canvas principal */}
                <div className="flex-1 relative overflow-hidden">
                    {(isStandaloneMode || (selectedAgent && activeView === 'funnel')) ? (
                        <div className="flex h-full">
                            {/* ReactFlow Canvas */}
                            <div className="flex-1 relative">
                              <WorkflowActionsContext.Provider value={{ addConnectedNode: handleAddConnectedNode }}>
                                <ReactFlow
                                    nodes={nodes}
                                    edges={edges}
                                    onNodesChange={onNodesChange}
                                    onEdgesChange={onEdgesChange}
                                    onConnect={onConnect}
                                    onMove={onMove}
                                    nodeTypes={nodeTypes}
                                    edgeTypes={edgeTypes}
                                    fitView
                                    fitViewOptions={{
                                        padding: 0.2,
                                        maxZoom: 1,
                                    }}
                                    multiSelectionKeyCode="Control"
                                    selectionKeyCode="Control"
                                    deleteKeyCode={null}
                                    onKeyDown={(e) => {
                                        // No borrar nodos si el usuario está escribiendo en un campo
                                        // de texto (textarea/input/select/contenteditable). Sin esto,
                                        // presionar Suprimir/Backspace para borrar texto elimina el nodo.
                                        const target = e.target as HTMLElement;
                                        const isEditing =
                                            target.tagName === 'TEXTAREA' ||
                                            target.tagName === 'INPUT' ||
                                            target.tagName === 'SELECT' ||
                                            target.isContentEditable;
                                        if (isEditing) return;

                                        if (e.key === 'Delete' || e.key === 'Backspace') {
                                            handleDeleteSelected();
                                        }
                                    }}
                                    className="bg-gray-50"
                                    connectionLineStyle={{ stroke: '#00FF7A', strokeWidth: 2 }}
                                    defaultEdgeOptions={{
                                        type: 'deletable',
                                        animated: true,
                                        style: { stroke: '#00FF7A', strokeWidth: 2 },
                                    }}
                                >
                                    <Controls />
                                    <MiniMap
                                        nodeColor={(node) => {
                                            if (node.type === 'textMessage') return '#00FF7A';
                                            if (node.type === 'imageMessage') return '#00A8FF';
                                            if (node.type === 'audioMessage') return '#a855f7';
                                            if (node.type === 'videoMessage') return '#f97316';
                                            if (node.type === 'documentMessage') return '#6366f1';
                                            return '#94a3b8';
                                        }}
                                    />
                                    <Background variant={BackgroundVariant.Dots} gap={12} size={1} />

                                    {/* Capa de grupos - dentro de ReactFlow */}
                                    <div className="react-flow__renderer" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}>
                                        {groups.map((group) => (
                                            <GroupNode
                                                key={group.id}
                                                group={group}
                                                viewport={viewport}
                                            />
                                        ))}
                                    </div>

                                    {/* Controles de grupos - capa superior */}
                                    <div className="react-flow__renderer" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 9999 }}>
                                        {groups.map((group) => (
                                            <GroupControls
                                                key={`controls-${group.id}`}
                                                group={group}
                                                viewport={viewport}
                                                onColorChange={(color: string) => updateGroupColor(group.id, color)}
                                                onInstructionChange={(instruction: string) => updateGroupInstruction(group.id, instruction)}
                                                onLeadStatusChange={(leadStatus: string) => updateGroupLeadStatus(group.id, leadStatus)}
                                                onProductKeysChange={(productKeys: string[]) => updateGroupProductKeys(group.id, productKeys)}
                                                onDelete={() => deleteGroup(group.id, edges, setEdges, setNodes, currentFlowId)}
                                                availableProducts={availableProducts}
                                            />
                                        ))}
                                    </div>
                                </ReactFlow>
                              </WorkflowActionsContext.Provider>
                            </div>

                            {/* WhatsApp Preview Panel */}
                            {currentFlowId && showWhatsappPreview && (
                                <div className={`bg-gray-50 overflow-hidden ${showWhatsappPreview ? 'w-[420px] opacity-100' : 'w-0 opacity-0'}`}>
                                    <WhatsAppPreviewPanel flowId={currentFlowId} onFlowDataChange={syncFlowData} />
                                </div>
                            )}
                        </div>
                    ) : !isStandaloneMode ? (
                        <AgentSelectionView onSelectAgent={setSelectedAgent} selectedAgentId={initialSelected} />
                    ) : null}
                </div>
            </div>

            {/* Modal Advertencia de Cambios No Guardados */}
            {(showUnsavedWarning || showNavigationWarning) && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
                    <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
                        <h3 className="text-lg font-semibold mb-4">⚠️ Cambios sin guardar</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            {showNavigationWarning
                                ? 'Estás a punto de salir de esta página con cambios sin guardar. ¿Qué deseas hacer?'
                                : 'Tienes cambios sin guardar. ¿Qué deseas hacer?'
                            }
                        </p>
                        <div className="flex flex-col gap-2">
                            <Button onClick={handleSaveAndContinue}>
                                Guardar y continuar
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleDiscardChanges}
                            >
                                Descartar cambios
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleCancelDiscard}
                            >
                                Cancelar
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Cargar Flujo */}
            {showLoadModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
                    <div className="bg-white rounded-lg w-[500px] shadow-xl">
                        <div className="p-4 border-b">
                            <h3 className="text-lg font-semibold">Cargar Flujo</h3>
                        </div>
                        <FlowSelector
                            currentFlowId={currentFlowId}
                            agentId={selectedAgent}
                        />
                        <div className="p-4 border-t">
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => setShowLoadModal(false)}
                            >
                                Cancelar
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {/* Floating WhatsApp preview toggle */}
            <div className="fixed right-6 bottom-6 z-[80]">
                <Button
                    size="sm"
                    variant="default"
                    className="h-14 w-14 rounded-full p-0 shadow-lg flex items-center justify-center"
                    onClick={() => {
                        const newState = !showWhatsappPreview;
                        setShowWhatsappPreview(newState);
                        try {
                            if (currentFlowId) {
                                localStorage.setItem(`whatsappPreview:${currentFlowId}`, JSON.stringify(newState));
                            } else {
                                localStorage.setItem('whatsappPreview:global', JSON.stringify(newState));
                            }
                        } catch (e) {
                            // ignore storage errors
                        }
                    }}
                    title={showWhatsappPreview ? 'Ocultar vista previa' : 'Mostrar vista previa'}
                >
                    <Smartphone className="w-14 h-14" />
                </Button>
            </div>
        </div>
    );
};

const AgentWorkflow: React.FC<{ agent?: Agent | null; trainingFlow?: any; loadingFlow?: boolean; isStandaloneMode?: boolean; workflowName?: string; onBack?: () => void }> = ({ agent, trainingFlow, loadingFlow, isStandaloneMode = false, workflowName, onBack }) => {
    return (
        <ReactFlowProvider>
            <AgentTrainingContent agent={agent} trainingFlow={trainingFlow} loadingFlow={loadingFlow} isStandaloneMode={isStandaloneMode} workflowName={workflowName} onBack={onBack} />
        </ReactFlowProvider>
    );
};

export default AgentWorkflow;
